import {
  MonoTypeOperatorFunction,
  Observable,
  retry,
  takeUntil,
  throwError,
  timer
} from "rxjs";

/**
 * Retry of the room load and join that follow a room socket (re)connect.
 *
 * Without it, one failed request (HTTP still down when the WebSocket is back,
 * a timeout, a 5xx) left the page connected but out of the room: invisible to
 * the others, with nothing retrying (staging qualification 2026-11-03). A
 * refusal is final: not found (the page leaves), forbidden, unauthorized
 * (sign-out), invalid input. A new connect resets the room and starts again:
 * the callers stop a pending retry on reset.
 */
export const ROOM_REJOIN_RETRIES = 8;
// Unauthorized, forbidden, not found, invalid input: the same answer again
const FINAL_CODES: readonly number[] = [1401, 1403, 1404, 1422];
const DELAY_MS = 1_000;
const DELAY_MAX_MS = 15_000;

export function isFinalRoomError(error: unknown): boolean {
  const code = (error as null | { code?: unknown })?.code;
  return typeof code === "number" && FINAL_CODES.includes(code);
}

// 1 s, 2 s, 4 s, 8 s, then 15 s, each +/- 50 % (about 1.5 min in all)
export function roomRejoinDelayMs(attempt: number, random = randomUnit()) {
  const base = Math.min(DELAY_MS * 2 ** Math.max(0, attempt - 1), DELAY_MAX_MS);
  return Math.round(base * (0.5 + random));
}

export function retryRoomRejoin<T>(
  retries = ROOM_REJOIN_RETRIES
): MonoTypeOperatorFunction<T> {
  return retry({
    count: retries,
    delay: (error, attempt) =>
      isFinalRoomError(error)
        ? throwError(() => error)
        : timer(roomRejoinDelayMs(attempt))
  });
}

/**
 * Retried as above, and stopped by `stop$` (the room reset of a new connect,
 * or its destroy): a pending retry never joins, nor publishes, for a reset
 * room.
 */
export function retryRoomRejoinUntil<T>(
  stop$: Observable<unknown>
): MonoTypeOperatorFunction<T> {
  return (source) => source.pipe(retryRoomRejoin<T>(), takeUntil(stop$));
}

function randomUnit() {
  const [value = 0] = crypto.getRandomValues(new Uint32Array(1));
  return value / 2 ** 32;
}
